import { useState } from 'react'
import { reminderService, ReminderConfig } from '../services/reminderService'
import { supabase } from '@/lib/supabase'

const toast = {
  success: (msg: string) => console.log('✅', msg),
  error: (msg: string) => console.log('❌', msg)
}

interface ReminderTestPreviewProps {
  config: ReminderConfig
  onClose: () => void
}

export function ReminderTestPreview({ config, onClose }: ReminderTestPreviewProps) {
  const [testEmail, setTestEmail] = useState('')
  const [isSending, setIsSending] = useState(false)

  const mockSlot = {
    volunteer_name: 'דנה כהן',
    date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    time_slot: '07:30-08:10',
    class_name: 'כיתה א-1'
  }

  const previewMessage = reminderService.formatMessage(config.message_template, mockSlot)

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error('אנא הזן כתובת דוא"ל')
      return
    }

    setIsSending(true)
    try {
      const { data, error } = await supabase.functions.invoke('send-test-reminder', {
        body: {
          testEmail: testEmail.trim(),
          reminderName: config.name,
          message: previewMessage,
          channel: config.channel,
        },
      })

      if (error) {
        throw error
      }

      if (data?.success) {
        toast.success(`הודעת בדיקה נשלחה ל-${testEmail} בהצלחה!`)
        setTestEmail('')
      } else {
        const errorMsg = data?.message || data?.error || 'Unknown error'
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Error sending test reminder:', error)
      const errorMsg = error instanceof Error ? error.message : 'שגיאה בשליחת הודעת הבדיקה'
      toast.error(errorMsg)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-l from-accent-600 to-accent-500 px-6 py-4 text-white font-hebrew flex items-center gap-3">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h2 className="text-2xl font-bold">
            תצוגה מקדימה ובדיקה
          </h2>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 font-hebrew">שם התזכורת</p>
                <p className="text-lg font-bold font-hebrew">{config.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-hebrew">ערוץ</p>
                <p className="text-lg font-bold font-hebrew">
                  {config.channel === 'email' && '📧 דוא"ל'}
                  {config.channel === 'whatsapp' && '💬 WhatsApp'}
                  {config.channel === 'sms' && '📱 SMS'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-hebrew">זמן הדיוק</p>
                <p className="text-lg font-bold font-hebrew">{config.hours_before} שעות</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-hebrew">סטטוס</p>
                <p className={`text-lg font-bold font-hebrew ${config.enabled ? 'text-success-600' : 'text-danger-600'}`}>
                  {config.enabled ? '✅ פעיל' : '❌ מושבת'}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 font-hebrew">
              📧 תצוגה מקדימה של ההודעה
            </h3>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 max-h-96 overflow-y-auto">
              <div className="text-sm text-gray-600 mb-3 font-hebrew">
                <p className="font-bold">נמען: {mockSlot.volunteer_name}</p>
              </div>
              <div className="whitespace-pre-wrap text-gray-800 font-hebrew text-sm leading-relaxed">
                {previewMessage}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 font-hebrew">
              זהו תצוגה מקדימה. נתונים בפועל יושלמו בהודעות המשלוחות בפועל.
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 font-hebrew">
              🧪 שלח הודעת בדיקה
            </h3>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder='כתובת דוא"ל לבדיקה'
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent font-hebrew"
              />
              <button
                onClick={handleSendTest}
                disabled={isSending}
                className="px-6 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 font-hebrew font-semibold transition-colors"
              >
                {isSending ? '...' : 'שלח בדיקה'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 font-hebrew">
              הודעת הבדיקה תישלח לכתובת שהוזנת כאן עם נתוני דוגמה.
            </p>
          </div>

          <div className="flex gap-3 justify-start pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-hebrew font-semibold transition-colors"
            >
              סגור
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
